import { useState } from 'react';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  useModal,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { ObjectType } from '@/services/db/object-type/entity';
import { useExportObjects } from '@/services/db/object/mutations';

const REQUIRED_ATTRIBUTES = ['object_id'] as const;

type ObjectExportModalData = {
  objectType: ObjectType;
  attributes: string[] | null;
  filters: string | null;
  isBroaderThanList: boolean;
};

function ObjectExportModal({ data, modalState, onClose, onConfirm }: PlumaManagedModalProps<ObjectExportModalData>) {
  const [attributes, setAttributes] = useState(data.attributes);
  const [includeRelationships, setIncludeRelationships] = useState(false);
  const snackbars = useSnackbars();
  const exportObjects = useExportObjects({
    mutationConfig: {
      onSuccess: () => {
        snackbars.create({ id: 'objects.export.success', message: 'Your export is being prepared.' });
        onConfirm();
      },
      onError: () =>
        snackbars.create({
          id: 'objects.export.error',
          variant: 'critical',
          message: "The export couldn't be started. Try again.",
        }),
    },
  });
  const choices = [...new Set([...REQUIRED_ATTRIBUTES, ...(data.objectType.sent_attributes ?? [])])];
  const toggleAttribute = (attribute: string, isChecked: boolean) => {
    if (attributes == null || attribute === 'object_id') return;
    setAttributes(isChecked ? [...attributes, attribute] : attributes.filter((value) => value !== attribute));
  };

  return (
    <Modal state={modalState} title={`Export ${data.objectType.name}`} isDismissable={!exportObjects.isPending}>
      <ModalBody>
        {data.isBroaderThanList ? (
          <Text color="subtle">
            Name searches and relationship views aren't applied to exports. This export may include more{' '}
            {data.objectType.name} than the current list.
          </Text>
        ) : null}
        {attributes == null ? (
          <Text>All attributes will be included.</Text>
        ) : (
          <Stack gap="100">
            {choices.map((attribute) => (
              <Checkbox
                key={attribute}
                label={attribute}
                value={attribute}
                isChecked={attributes.includes(attribute)}
                isDisabled={attribute === 'object_id'}
                onCheckedChange={(isChecked) => toggleAttribute(attribute, isChecked)}
              />
            ))}
          </Stack>
        )}
        {data.objectType.object_count > 0 ? (
          <Checkbox
            mt="200"
            label="Include relationships"
            value="include_relationships"
            isChecked={includeRelationships}
            onCheckedChange={setIncludeRelationships}
          />
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} isDisabled={exportObjects.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isLoading={exportObjects.isPending}
          onClick={() =>
            exportObjects.mutate({
              objectTypeId: data.objectType.id,
              body: {
                attributes,
                export_relationships: includeRelationships,
                exported_from: 'object_list',
                filters: data.filters,
              },
            })
          }
        >
          Export
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type ObjectExportProps = {
  objectType: ObjectType;
  displayedAttributes: readonly string[];
  filters: string | null;
  isDisabled: boolean;
  isBroaderThanList: boolean;
};

export function ObjectExport({
  objectType,
  displayedAttributes,
  filters,
  isDisabled,
  isBroaderThanList,
}: ObjectExportProps) {
  const modal = useModal(ObjectExportModal);
  const open = (attributes: string[] | null) =>
    modal.open({ objectType, attributes, filters, isBroaderThanList }, { shouldCloseOnUnmount: true });

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton isDisabled={isDisabled} icon="chevron-down" iconPosition="trailing">
        Export to CSV
      </DropdownMenuTriggerButton>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => open(null)}>All attributes</DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => open([...new Set([...displayedAttributes, 'object_id', 'name', 'created_at'])])}
        >
          Displayed attributes
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => open([...REQUIRED_ATTRIBUTES])}>Choose attributes</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
